/**
 * Parser for Connection step template data (Basic group)
 * Matches Copilot1's connection parsing implementation
 */

import type { Dispatch } from '@reduxjs/toolkit';

import {
  setSavedStepData,
  updateStepFormData,
} from '../../data-store/copilotSlice';

import { extractConnectionInstructions } from './instructionUtils';
import type { ParsedField, TemplateAttribute, TemplateGroup } from './types';

// Type definitions for ConnectionJSON structure
interface AccountAuthentication {
  targetURL?: string;
  targetUsername?: string;
  targetPassword?: string;
}

interface ConnectionAuthentications {
  acctAuth?: AccountAuthentication;
}

interface ConnectionData {
  authentications?: ConnectionAuthentications;
}

const isValidationRequired = ['externalconnattvalue.URL'];

/**
 * Extract instructions from ConnectionJSON attribute
 */
const getConnectionInstructions = (basicGroup: TemplateGroup): string => {
  const connectionJSONAttr = basicGroup.attributes.find(
    (attr: TemplateAttribute) =>
      attr.attributeName === 'externalconnattvalue.ConnectionJSON'
  );

  return connectionJSONAttr?.attributeValue
    ? extractConnectionInstructions(connectionJSONAttr.attributeValue)
    : '';
};

const parseConnectionStep = (
  groups: TemplateGroup[],
  dispatch?: Dispatch
): ParsedField[] & { instructions?: string } => {
  // Connection step maps to "Basic" group in Copilot1
  // Create deep copy to avoid read-only issues with frozen template objects
  // This is necessary because the original groups object has read-only properties
  // that prevent direct modification of attributeValue for step validation
  const typedGroups = JSON.parse(JSON.stringify(groups)) as TemplateGroup[];
  const basicGroup = typedGroups.find(
    (group) => group.name.toLowerCase() === 'basic'
  );

  if (!basicGroup) {
    return [];
  }

  const fields: ParsedField[] = [];
  const connectionInstructions = getConnectionInstructions(basicGroup);

  basicGroup.attributes.forEach((attr: TemplateAttribute) => {
    if (!attr.displayOnUI) {
      return;
    }

    const isRequired = attr.mandatory.toLowerCase() === 'yes';
    const defaultVal = attr.attributeValue ?? '';
    const placeholder = attr.attributeValueExample ?? '';

    // Add tooltip for URL fields
    const isTooltip =
      attr.attributeName && isValidationRequired.includes(attr.attributeName);
    const tooltipText = isTooltip
      ? 'INTEGRATION_COPILOT_CONNECTION_URL_TOOLTIP'
      : undefined;

    // Add placeholders for common fields
    const placeholders: Record<string, string> = {
      'externalconnattvalue.USERNAME':
        'INTEGRATION_APPLICATION_USERNAME_PLACEHOLDER',
      'externalconnattvalue.PASSWORD':
        'INTEGRATION_APPLICATION_PASSWORD_PLACEHOLDER',
      'externalconnattvalue.URL': 'INTEGRATION_APPLICATION_URL',
    };
    const finalPlaceholder =
      (attr.attributeName && placeholders[attr.attributeName]) || placeholder;

    if (
      attr.formatDesc.toLowerCase().includes('textfield') ||
      attr.formatDesc.includes('Password text field')
    ) {
      fields.push({
        type: 'TextField' as const,
        label: attr.attributeDisplayName ?? '',
        attributeName: attr.attributeName,
        payloadAtrributeName: attr.attributeName,
        attributeKey: String(attr.attributeKey),
        required: isRequired,
        defaultValue: defaultVal,
        attributeValue: attr.attributeValue,
        placeholder: finalPlaceholder,
        kind: attr.formatDesc.toLowerCase().includes('multiline')
          ? 'multiline'
          : 'singleline',
        password: attr.formatDesc.toLowerCase().includes('password'),
        pattern: attr.validationPattern ?? undefined,
        isTooltip: Boolean(isTooltip),
        tooltipText,
        tooltipIconColor: isTooltip
          ? '--color-foreground-info-subtle'
          : undefined,
      });
    } else if (attr.formatDesc.toLowerCase() === 'singleselect dropdown') {
      fields.push({
        type: 'ToggleGroup' as const,
        label: attr.attributeDisplayName ?? '',
        attributeName: attr.attributeName,
        attributeKey: String(attr.attributeKey),
        options: [],
        required: isRequired,
        payloadAtrributeName: attr.attributeName,
      });
    } else if (
      attr.formatDesc.toLowerCase().includes('singleselect dropdown object')
    ) {
      fields.push({
        type: 'Dropdown' as const,
        label: attr.attributeDisplayName ?? '',
        attributeName: attr.attributeName,
        payloadAtrributeName: attr.attributeName,
        placeholder: finalPlaceholder,
        attributeKey: String(attr.attributeKey),
        options: [],
        apiURL: attr.apiURL ?? '',
        required: isRequired,
        fieldDisabled: false,
      });
    }
  });

  // Populate stepFormData from ConnectionJSON (both edit and create mode)
  if (dispatch) {
    const stepFormData: Record<string, unknown> = {};

    // Find ConnectionJSON attribute
    const connectionJSONAttr = basicGroup.attributes.find(
      (attr: TemplateAttribute) =>
        attr.attributeName === 'externalconnattvalue.ConnectionJSON'
    );

    if (connectionJSONAttr?.attributeValue) {
      try {
        const connectionData = JSON.parse(
          connectionJSONAttr.attributeValue
        ) as ConnectionData;
        const acctAuth = connectionData.authentications?.acctAuth;

        if (acctAuth) {
          // Map ConnectionJSON values to field names for form display
          // and update field configurations for step validation
          if (acctAuth.targetURL) {
            // Update stepFormData for form field population
            stepFormData['externalconnattvalue.URL'] = acctAuth.targetURL;

            // Update field configuration for step completion validation
            // Step validation checks field.attributeValue, so we need to update both
            // stepFormData (for UI) and field.attributeValue (for validation)
            const urlFieldIndex = basicGroup.attributes.findIndex(
              (attr: TemplateAttribute) =>
                attr.attributeName === 'externalconnattvalue.URL'
            );

            if (urlFieldIndex !== -1) {
              // Replace entire field object to update read-only attributeValue
              basicGroup.attributes[urlFieldIndex] = {
                ...basicGroup.attributes[urlFieldIndex],
                attributeValue: acctAuth.targetURL,
              };
            }
          }

          if (acctAuth.targetUsername) {
            stepFormData['externalconnattvalue.USERNAME'] =
              acctAuth.targetUsername;

            // Update field configuration for step completion validation
            const usernameFieldIndex = basicGroup.attributes.findIndex(
              (attr: TemplateAttribute) =>
                attr.attributeName === 'externalconnattvalue.USERNAME'
            );

            if (usernameFieldIndex !== -1) {
              basicGroup.attributes[usernameFieldIndex] = {
                ...basicGroup.attributes[usernameFieldIndex],
                attributeValue: acctAuth.targetUsername,
              };
            }
          }

          if (acctAuth.targetPassword) {
            stepFormData['externalconnattvalue.PASSWORD'] =
              acctAuth.targetPassword;

            // Update field configuration for step completion validation
            const passwordFieldIndex = basicGroup.attributes.findIndex(
              (attr: TemplateAttribute) =>
                attr.attributeName === 'externalconnattvalue.PASSWORD'
            );

            if (passwordFieldIndex !== -1) {
              basicGroup.attributes[passwordFieldIndex] = {
                ...basicGroup.attributes[passwordFieldIndex],
                attributeValue: acctAuth.targetPassword,
              };
            }
          } else {
            stepFormData['externalconnattvalue.PASSWORD'] = '';
          }
        }
      } catch (error) {
        // Silently handle JSON parsing errors - invalid ConnectionJSON format
        // This prevents the parser from breaking when ConnectionJSON is malformed
      }
    }

    if (Object.keys(stepFormData).length > 0 || connectionInstructions) {
      if (connectionInstructions) {
        stepFormData.instructions = connectionInstructions;
      }

      dispatch(
        updateStepFormData({ stepName: 'Connection', formData: stepFormData })
      );
      dispatch(
        setSavedStepData({ stepName: 'Connection', formData: stepFormData })
      );
    } else {
      // In create mode, initialize with default empty values for save draft baseline
      const defaultStepFormData = {
        'externalconnattvalue.URL': '',
        'externalconnattvalue.USERNAME': '',
        'externalconnattvalue.PASSWORD': '',
      };

      dispatch(
        updateStepFormData({
          stepName: 'Connection',
          formData: defaultStepFormData,
        })
      );
      dispatch(
        setSavedStepData({
          stepName: 'Connection',
          formData: defaultStepFormData,
        })
      );
    }
  }

  // Return fields with instructions property
  return Object.assign(
    fields,
    connectionInstructions ? { instructions: connectionInstructions } : {}
  );
};

export default parseConnectionStep;
